import { Star } from 'lucide-react';

/**
 * Star rating display. Half-star precision via clip-path.
 *
 *   <Stars value={4.5} reviews={128} />
 *   <Stars value={5} size={20} />
 */

export interface StarsProps {
    value: number;
    reviews?: number;
    size?: number;
    className?: string;
}

export function Stars({ value, reviews, size = 14, className }: StarsProps) {
    const clamped = Math.max(0, Math.min(5, value));
    const cls = ['inline-flex items-center gap-1', className ?? '']
        .filter(Boolean)
        .join(' ');

    return (
        <span
            className={cls}
            role="img"
            aria-label={
                reviews !== undefined
                    ? `${clamped.toFixed(1)} out of 5 stars from ${reviews} reviews`
                    : `${clamped.toFixed(1)} out of 5 stars`
            }
        >
            <span className="inline-flex">
                {[0, 1, 2, 3, 4].map((i) => {
                    const fill = Math.max(0, Math.min(1, clamped - i));
                    return (
                        <span
                            key={i}
                            className="relative inline-block"
                            style={{ width: size, height: size }}
                        >
                            <Star
                                size={size}
                                className="absolute inset-0 text-rule"
                                strokeWidth={1.5}
                            />
                            <span
                                className="absolute inset-0 overflow-hidden"
                                style={{ width: `${fill * 100}%` }}
                            >
                                <Star
                                    size={size}
                                    className="text-gold"
                                    fill="currentColor"
                                    strokeWidth={1.5}
                                />
                            </span>
                        </span>
                    );
                })}
            </span>
            {reviews !== undefined && (
                <span className="text-[12px] text-ink-mute" aria-hidden="true">
                    ({reviews.toLocaleString()})
                </span>
            )}
        </span>
    );
}

export default Stars;
